import { Box, Chip, Divider, Typography } from '@mui/material';
import { LabelValueRow } from '../LabelValueRow';
import { BreakdownRow } from '../BreakdownRow';
import { formatCurrency } from '../../../../shared/utils/currency';
import type { PaymentRowDto } from '../../api/paymentsApi';

interface PaymentDetailPanelContentProps {
  payment: PaymentRowDto;
  /** Individual pot rows when this payment is an aggregated worker row. */
  breakdownRows?: PaymentRowDto[];
}

export function PaymentDetailPanelContent({
  payment,
  breakdownRows,
}: PaymentDetailPanelContentProps) {
  const hasBreakdown = breakdownRows && breakdownRows.length > 1;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
      <LabelValueRow label="Person Name" value={payment.personName} />
      {!hasBreakdown && (
        <LabelValueRow label="Pot Reference" value={payment.potReference} />
      )}
      <LabelValueRow
        label="Status"
        value={
          <Chip
            label={payment.potStatus}
            size="small"
            color={
              payment.potStatus === 'Paid' ||
              payment.potStatus === 'Partially Paid'
                ? 'success'
                : 'default'
            }
            variant={payment.potStatus === 'Paid' ? 'filled' : 'outlined'}
          />
        }
      />

      <Divider />

      <Typography variant="subtitle2">Bank Details</Typography>
      <LabelValueRow
        label="Account Number"
        value={payment.accountNumber || '--'}
      />
      <LabelValueRow label="Sort Code" value={payment.sortCode || '--'} />
      <LabelValueRow label="Company Name" value={payment.companyName || '--'} />

      <Divider />

      <LabelValueRow
        label="Total Due"
        value={formatCurrency(payment.amountDue)}
        valueWeight="medium"
      />

      {hasBreakdown && (
        <>
          <Divider />
          <Typography variant="subtitle2">Breakdown</Typography>
          <Box
            data-testid="payment-breakdown"
            sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}
          >
            {breakdownRows.map((row, i) => (
              <BreakdownRow
                key={`${row.potReference}-${i}`}
                row={row}
                data-testid={`payment-breakdown-row-${i}`}
              />
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}
